import * as React from 'react';
import * as SwitchPrimitives from '@radix-ui/react-switch';
import { motion, type HTMLMotionProps } from 'framer-motion';
import { useState } from 'react';

const MotionSwitch = motion.create(SwitchPrimitives.Root);
const MotionThumb = motion.create(SwitchPrimitives.Thumb);

const Switch = React.forwardRef<
	React.ElementRef<typeof SwitchPrimitives.Root>,
	React.ComponentPropsWithoutRef<typeof SwitchPrimitives.Root> &
		HTMLMotionProps<'button'> & {
			checked?: boolean;
			onCheckedChange?: (checked: boolean) => void;
		}
>(({ checked: controlledChecked, onCheckedChange, ...props }, ref) => {
	const [internalChecked, setInternalChecked] = useState(props.defaultChecked || false);

	const isControlled = controlledChecked !== undefined;
	const checkedState = isControlled ? controlledChecked : internalChecked;

	const handleCheckedChange = (newChecked: boolean) => {
		if (!isControlled) {
			setInternalChecked(newChecked);
		}
		onCheckedChange?.(newChecked);
	};

	const thumbVariants = {
		checked: {
			x: '18px',
		},
		unchecked: {
			x: '2px',
		},
	};

	return (
		<MotionSwitch
			checked={checkedState}
			onCheckedChange={handleCheckedChange}
			className='inline-flex items-center w-[36px] h-5 shrink-0 cursor-pointer bg-gray-200 rounded-full relative data-[state=checked]:bg-gray-900 focus-visible:outline-none focus-visible:ring-2 transition-all focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-gray-900/50 disabled:cursor-not-allowed disabled:opacity-50'
			animate={checkedState ? 'checked' : 'unchecked'}
			{...props}
			ref={ref}>
			<MotionThumb
				initial={{ x: '2px' }}
				className='block size-4 bg-white rounded-full shadow-sm'
				variants={thumbVariants}
				transition={{
					type: 'spring',
					visualDuration: 0.2,
					bounce: 0.2,
				}}
			/>
		</MotionSwitch>
	);
});
Switch.displayName = SwitchPrimitives.Root.displayName;

export default Switch;
